import { isArray, isNil, AuctionStatuses } from 'vdlib';
import LookupsHelper from './LookupsHelper';
import Req from './RequestHelper';

const toServerRectangle = ({ x, y, width, height }) => ({
  X: x || 0,
  Y: y || 0,
  Width: width || 0,
  Height: height || 0,
});

const Api = {
  /* App */
  getInitialData: () => Req.postJson('app/initialdata', { ApplicationID: 1 }), //Parts
  login: credentials =>
    Req.anonymousPostJson('identity/authenticate', credentials),
  changeLanguage: locale =>
    Req.postJson('app/changelanguage', { Lang: locale }),

  changePassword: ({ oldPassword, newPassword }) =>
    Req.postJson('identity/changepassword', {
      OldPassword: oldPassword,
      NewPassword: newPassword,
    }),
  resetPassword: partyId =>
    Req.postJson('identity/resetpassword', { PartyId: partyId }),

  /* Dashboard */
  fetchDashboardData: () => Req.getJson('parts/app/dashboard/get'),

  /* Folders */
  searchFoldersByNumber: searchParam =>
    Req.postJson('folders/query', { Number: searchParam }),

  /* Auctions */
  fetchAuction: auctionId =>
    Req.postJson('parts/auctions/query', { AuctionId: parseInt(auctionId) }),
  fetchCurrentAuctions: skipStep =>
    Req.postJson('parts/auctions/querysummary', {
      Statuses: [
        LookupsHelper.getIdByDescription(
          'auctionStatuses',
          AuctionStatuses.Active
        ),
      ],
      SkipStep: skipStep,
    }),
  fetchPendingApprovalAuctions: skipStep =>
    Req.postJson('parts/auctions/querysummary', {
      Statuses: [
        LookupsHelper.getIdByDescription(
          'auctionStatuses',
          AuctionStatuses.Pending
        ),
      ],
      SkipStep: skipStep,
    }),
  fetchForSelectionAuctions: skipStep =>
    Req.postJson('parts/auctions/querysummary', {
      Statuses: [
        LookupsHelper.getIdByDescription(
          'auctionStatuses',
          AuctionStatuses.ForSelection
        ),
      ],
      SkipStep: skipStep,
    }),
  fetchForAcceptanceAuctions: skipStep =>
    Req.postJson('parts/auctions/querysummary', {
      Statuses: [
        LookupsHelper.getIdByDescription(
          'auctionStatuses',
          AuctionStatuses.ForAcceptance
        ),
      ],
      SkipStep: skipStep,
    }),
  fetchForDispatchAuctions: skipStep =>
    Req.postJson('parts/auctions/querysummary', {
      Statuses: [
        LookupsHelper.getIdByDescription(
          'auctionStatuses',
          AuctionStatuses.ForDispatch
        ),
      ],
      SkipStep: skipStep,
    }),
  fetchCompletedAuctions: skipStep =>
    Req.postJson('parts/auctions/querysummary', {
      Statuses: [
        LookupsHelper.getIdByDescription(
          'auctionStatuses',
          AuctionStatuses.Finished
        ),
      ],
      SkipStep: skipStep,
    }),
  fetchLostAuctions: () => Req.postJson('parts/auctions/lost_querysummary'),
  fetchRejectedAuctions: () =>
    Req.postJson('parts/auctions/rejected_querysummary'),
  fetchDispatchProblemAuctions: () =>
    Req.postJson('parts/auctions/dispatchproblem_querysummary'),
  fetchParticipateToAuctions: () =>
    Req.postJson('parts/auctions/querysummary', {
      Statuses: [
        LookupsHelper.getIdByDescription(
          'auctionStatuses',
          AuctionStatuses.Active
        ),
      ],
      IsParticipate: true,
    }),
  fetchMerchantAuctionsSummary: () =>
    Req.postJson('parts/auctions/merchant_active_querysummary'),

  fetchPartHistory: auctionId =>
    Req.postJson('parts/auctions/gethistory', { AuctionID: auctionId }),

  uploadAuction: ({ expertId, insuranceId, garage, file }) =>
    Req.uploadFile('upload/estimation/parts', file, {
      InsuranceID: insuranceId,
      ExpertID: expertId,
      Garage: garage,
    }),
  createAuction: payload =>
    Req.postJson('parts/auctions/create', {
      Auction: payload.auctionData,
      Folder: payload.folderData,
      Garage: payload.garageData,
    }),
  editAuction: payload =>
    Req.postJson('parts/auctions/edit-single', {
      Auction: payload.auctionData,
      Folder: payload.folderData,
    }),
  editAuctions: payload =>
    Req.postJson('parts/auctions/edit', {
      Auction: payload.auctionData,
      Folder: payload.folderData,
    }),
  commentAuction: ({ auctionId, auctionStatusId, comments }) =>
    Req.postJson('parts/auctions/addcomments', {
      AuctionID: auctionId,
      AuctionStatusID: auctionStatusId,
      Comments: comments,
    }),

  startAuctions: ({ auctions, comments, useDefaultValues }) =>
    Req.postJson('parts/auctions/start', {
      IsDefault: useDefaultValues === true,
      Auctions: auctions,
      Comments: comments,
    }),
  endAuction: ({ bidId, garageData }) =>
    Req.postJson('parts/auctions/end', { BidID: bidId, Garage: garageData }),
  endAuctions: ({ auctionIds, filters }) =>
    Req.postJson('parts/auctions/end_selected', {
      AuctionsIDs: auctionIds,
      Filters: filters ? JSON.stringify(filters) : '',
    }),
  acceptAuction: ({ auctionId, garageId }) =>
    Req.postJson('parts/auctions/accept', {
      AuctionID: auctionId,
      GarageID: garageId,
    }),
  rejectAuction: ({ auctionId, comments }) =>
    Req.postJson('parts/auctions/reject', {
      AuctionID: auctionId,
      Comments: comments,
    }),
  cancelAuctions: ({ auctionIds }) =>
    Req.postJson('parts/auctions/cancel', {
      Auctions: (isArray(auctionIds) ? auctionIds : []).map(id => ({
        ID: id,
      })),
    }),
  dispatchAuction: ({ auctionId, isNotDispatch, comments }) =>
    Req.postJson('parts/auctions/dispatch', {
      AuctionID: auctionId,
      IsNotDispatched: isNotDispatch,
      Comments: comments,
    }),

  exportPdfAuctions: ({ auctionIds, statusID, filters, selected }) =>
    Req.postJson('parts/reports/export_to_pdf_auctions', {
      AuctionsIDs: auctionIds,
      StatusID: statusID,
      Filters: filters ? JSON.stringify(filters) : '',
      Selected: selected,
    }),

  fetchCommentsForAuction: auctionId =>
    Req.postJson('parts/auctions/comments', { AuctionId: auctionId }),

  /* Bids */
  fetchBidsForAuction: auctionId =>
    Req.postJson('parts/bids/query', {
      AuctionId: auctionId,
      Statuses: [1, 2],
    }),
  createBids: ({ auctionId, bidData }) =>
    Req.postJson('parts/bids/create', {
      AuctionID: auctionId,
      Bids: isArray(bidData) ? bidData : [bidData],
    }),
  cancelBid: bidId => Req.postJson('parts/bids/cancel', { BidIds: [bidId] }),
  tempSelectBid: bidId =>
    Req.postJson('parts/bids/tempselect', { BidIds: [bidId] }),

  /* Parties */
  fetchParty: partyId =>
    Req.postJson('parties/query', { PartyId: parseInt(partyId) }),
  createParty: ({ PartyAppMemberships, RelatedPartyIDs, ...partyData }) =>
    Req.postJson('parties/create', {
      Party: partyData,
      PartyAppMemberships,
      RelatedPartyIDs: isArray(RelatedPartyIDs)
        ? RelatedPartyIDs
        : [RelatedPartyIDs],
    }),
  updateParty: ({ PartyAppMemberships, RelatedPartyIDs, ...partyData }) =>
    Req.postJson('parties/edit', {
      Party: partyData,
      PartyAppMemberships,
      RelatedPartyIDs: isArray(RelatedPartyIDs)
        ? RelatedPartyIDs
        : isNil(RelatedPartyIDs)
        ? []
        : [RelatedPartyIDs],
    }),
  deleteParty: partyId =>
    Req.postJson('parties/delete', {
      PartyId: partyId,
    }),
  enableDisableParty: partyId =>
    Req.postJson('parties/enable_disable', {
      PartyId: partyId,
    }),
  fetchPartiesData: () => Req.postJson('parties/query'),

  /* Garages */
  createGarage: garageData =>
    Req.postJson('garages/create', { Garage: garageData }),
  updateGarage: garageData =>
    Req.postJson('garages/edit', { Garage: garageData }),
  deleteGarage: id =>
    Req.postJson('garages/delete', { GarageId: parseInt(id) }),
  fetchGarage: id => Req.postJson('garages/query', { GarageId: parseInt(id) }),
  fetchGaragesData: payload => Req.postJson('garages/query', payload),

  /* Reports */
  fetchReportData: ({ role, dateFrom, dateTo, group, periodType, dataGroup }) =>
    Req.postJson(`parts/reports/${role}_reports`, {
      DateFrom: dateFrom,
      DateTo: dateTo,
      Group: group,
      PeriodType: periodType,
      DataGroup: dataGroup || 0,
    }),

  /* Images */
  fetchFolderImages: folderId =>
    Req.postJson('parts/auctions/images', { FolderId: folderId }),

  uploadFolderImages: ({ folderId: FolderId, images }) =>
    Req.uploadFile('uploadmultiple/parts/auctions/images', images, {
      FolderId,
    }),
  deleteFolderImages: ({ folderId: FolderId, imageIds: ImageIds }) =>
    Req.postJson('parts/auctions/images/delete', {
      FolderId,
      ImageIds,
    }),
  reorderFolderImages: ({ folderId: FolderId, imageIds: OrderedImageIds }) =>
    Req.postJson('parts/auctions/images/reorder', {
      FolderId,
      OrderedImageIds,
    }),
  removeImageLicensePlate: ({
    folderId: FolderId,
    imageId: ImageId,
    rectangle,
    container,
  }) =>
    Req.postJson('parts/auctions/images/remove-license-plate', {
      FolderId,
      ImageId,
      Mask: toServerRectangle(rectangle),
      Container: toServerRectangle(container),
      RemoveMethod: window.VDGLOBALS.licenseRemoveMethod || 'blur',
    }),
  uploadPartImages: ({
    auctionId: AuctionId,
    images,
    merchantId,
    partTypeId,
  }) =>
    Req.uploadFile('uploadmultiple/parts/auctions/parts/images', images, {
      AuctionId,
      MerchantId: merchantId,
      PartTypeId: partTypeId,
    }),
  fetchAuctionPartImages: ({ auctionId, merchantId, partTypeId }) =>
    Req.postJson('parts/auctions/parts/images', {
      AuctionId: auctionId,
      MerchantId: merchantId,
      PartTypeId: partTypeId,
    }),
  deleteAuctionPartImages: ({ imageIds: ImageIds }) =>
    Req.postJson('parts/auctions/parts/images/delete', {
      ImageIds,
    }),
};

export default Api;
